/**
 * SavePresetModal Component
 *
 * Modal for saving current filter settings as a new preset.
 */

import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { usePresets } from '../../hooks/usePresets';
import { AdvancedFilters } from '../screener/filters/types';
import { ContractFilters, PresetCreate } from '../../types/presets';
import './SavePresetModal.css';

interface SavePresetModalProps {
  isOpen: boolean;
  onClose: () => void;
  screenerFilters?: AdvancedFilters;
  contractFilters?: ContractFilters;
  onPresetSaved?: (presetId: number) => void;
}

const SavePresetModal: React.FC<SavePresetModalProps> = ({
  isOpen,
  onClose,
  screenerFilters = {},
  contractFilters = {},
  onPresetSaved
}) => {
  const { createPreset } = usePresets();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Please enter a name for your preset');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const presetData: PresetCreate = {
        name: name.trim(),
        description: description.trim() || undefined,
        // Screener filters
        iv_rank_min: screenerFilters.iv_rank_min,
        iv_rank_max: screenerFilters.iv_rank_max,
        annualized_yield_min: screenerFilters.annualized_yield_min,
        annualized_yield_max: screenerFilters.annualized_yield_max,
        spread_pct_min: screenerFilters.spread_pct_min,
        spread_pct_max: screenerFilters.spread_pct_max,
        market_cap_categories: screenerFilters.market_cap_categories,
        sectors: screenerFilters.sectors,
        net_margin_min: screenerFilters.net_margin_min,
        net_margin_max: screenerFilters.net_margin_max,
        debt_equity_max: screenerFilters.debt_equity_max,
        dividend_yield_min: screenerFilters.dividend_yield_min,
        dividend_yield_max: screenerFilters.dividend_yield_max,
        earnings_within_days: screenerFilters.earnings_within_days,
        exclude_earnings_within_days: screenerFilters.exclude_earnings_within_days,
        rsi_min: screenerFilters.rsi_min,
        rsi_max: screenerFilters.rsi_max,
        trend_signals: screenerFilters.trend_signals,
        // Contract filters
        dte_min: contractFilters.dte_min,
        dte_max: contractFilters.dte_max,
        delta_min: contractFilters.delta_min,
        delta_max: contractFilters.delta_max,
        aroc_min: contractFilters.aroc_min,
        aroc_max: contractFilters.aroc_max,
        contract_iv_min: contractFilters.contract_iv_min,
        contract_iv_max: contractFilters.contract_iv_max,
        contract_spread_max: contractFilters.contract_spread_max,
        open_interest_min: contractFilters.open_interest_min,
        theta_min: contractFilters.theta_min,
        theta_max: contractFilters.theta_max,
        gamma_min: contractFilters.gamma_min,
        gamma_max: contractFilters.gamma_max,
        vega_min: contractFilters.vega_min,
        vega_max: contractFilters.vega_max,
      };

      const newPreset = await createPreset(presetData);
      if (newPreset) {
        setName('');
        setDescription('');
        if (onPresetSaved) {
          onPresetSaved(newPreset.id);
        }
        onClose();
      } else {
        setError('Failed to save preset. Please try again.');
      }
    } catch (err) {
      console.error('Error saving preset:', err);
      setError(err instanceof Error ? err.message : 'Failed to save preset');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setError(null);
    onClose();
  };

  // Count active filters
  const activeFilterCount = Object.values({ ...screenerFilters, ...contractFilters })
    .filter(v => v !== undefined && v !== null && (Array.isArray(v) ? v.length > 0 : true))
    .length;

  return (
    <div className="save-preset-modal-overlay" onClick={handleClose}>
      <div className="save-preset-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="save-preset-modal-header">
          <h2>Save as Preset</h2>
          <button className="save-preset-close-btn" onClick={handleClose}>
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="save-preset-modal-content">
          <div className="save-preset-form">
            {/* Name input */}
            <div className="save-preset-field">
              <label htmlFor="preset-name">Name *</label>
              <input
                id="preset-name"
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="My Preset"
                maxLength={100}
                autoFocus
              />
            </div>

            {/* Description input */}
            <div className="save-preset-field">
              <label htmlFor="preset-description">Description (optional)</label>
              <textarea
                id="preset-description"
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Brief description of this preset..."
                rows={3}
              />
            </div>

            {/* Filter summary */}
            <div className="save-preset-summary">
              <span className="save-preset-summary-label">Filters to save:</span>
              <span className="save-preset-summary-count">{activeFilterCount} active filters</span>
            </div>

            {/* Error message */}
            {error && (
              <div className="save-preset-error">{error}</div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="save-preset-modal-footer">
          <button className="save-preset-cancel-btn" onClick={handleClose}>
            Cancel
          </button>
          <button
            className="save-preset-save-btn"
            onClick={handleSave}
            disabled={isSaving || !name.trim()}
          >
            {isSaving ? 'Saving...' : 'Save Preset'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SavePresetModal;
